import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PricingTable, Season } from '../types';

const MIN_PEOPLE = 4;
const MIN_PEOPLE_CHRISTMAS = 5;
const EXTRA_BED_PRICE = 350;
const LATE_ARRIVAL_FEE = 500;

const priceTable: PricingTable = {
    vanoce: { 4: 3000, 5: 3750, 6: 4500, 7: 5250, 8: 6000 },
    zimni1: { 4: 2200, 5: 2750, 6: 3300, 7: 3850, 8: 4400 },
    zimni2: { 4: 2400, 5: 3000, 6: 3600, 7: 4200, 8: 4800 },
    leto: { 4: 2200, 5: 2750, 6: 3300, 7: 3850, 8: 4400 },
    mimosezona: { 4: 2000, 5: 2500, 6: 3000, 7: 3500, 8: 4000 },
};

const detectSeason = (date: Date): Season => {
    const m = date.getMonth() + 1;
    const d = date.getDate();
    if ((m === 12 && d >= 21) || (m === 1 && d <= 2)) return 'vanoce';
    if ((m === 1 && d >= 3) || m === 2 || m === 3) return 'zimni2';
    if (m >= 6 && m <= 8) return 'leto';
    if (m === 4 || m === 5) return 'mimosezona';
    if (m === 9 || m === 10 || m === 11 || (m === 12 && d <= 20)) return 'zimni1';
    return 'mimosezona';
};

interface UsePricingProps {
    startDate: Date | null;
    endDate: Date | null;
    adults: number;
    children: number;
    needsExtraBed: boolean;
    arrivalTime: string;
}

export const usePricing = ({
    startDate,
    endDate,
    adults,
    children,
    needsExtraBed,
    arrivalTime,
}: UsePricingProps) => {
    const { t } = useTranslation();
    const totalPeople = adults + children;

    return useMemo(() => {
        if (startDate && endDate) {
            const msPerDay = 1000 * 60 * 60 * 24;
            const rawNights = Math.ceil((endDate.getTime() - startDate.getTime()) / msPerDay);
            const nights = Math.max(rawNights, 0);

            const season = detectSeason(startDate);
            const minPeople = season === 'vanoce' ? MIN_PEOPLE_CHRISTMAS : MIN_PEOPLE;
            const effectivePeople = totalPeople < minPeople ? minPeople : totalPeople;
            const basePerNight = priceTable[season][effectivePeople] ?? 0;

            const extraBedCharge = needsExtraBed && totalPeople === 8 ? EXTRA_BED_PRICE * nights : 0;
            const arrivalHour = parseInt(arrivalTime.split(':')[0], 10);
            const lateFee = arrivalHour >= 21 ? LATE_ARRIVAL_FEE : 0;

            const total = basePerNight * nights + extraBedCharge + lateFee;

            return {
                totalPrice: total,
                lateArrivalFee: lateFee,
                nights,
                season,
            };
        }
        return { totalPrice: 0, lateArrivalFee: 0, nights: 0, season: 'mimosezona' as Season };
    }, [startDate, endDate, totalPeople, needsExtraBed, arrivalTime]);
};
